import { Image } from 'expo-image';
import { router, useLocalSearchParams } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';

import { useCreateRedirect } from '@/api/hooks/authQueryHooks';
import { useGetNonProfitPaymentMethods } from '@/api/hooks/nonProfitOrgHooks';
import { donation } from '@/assets/images';
import GoBackButton from '@/components/buttons/GoBackButton';
import DonationForm from '@/components/forms/DonationForm';
import FormWrapper from '@/components/forms/FormWrapper';
import Text from '@/components/Text';
import Title from '@/components/Title';
import PlatformContainer from '@/components/web/PlatformContainer';
import { APPSFLYER_CONFIG } from '@/constants/config';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useInfoModal } from '@/contexts/infoModalContext';
import { DonationData } from '@/lib/validations/DonationSchema';
import { encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const Donation = () => {
  const { state } = useAuth();
  const { npoId, npoName, amount, isIcac } = useLocalSearchParams<{
    npoId?: string;
    npoName?: string;
    amount?: string;
    isIcac?: string;
  }>();
  const { width } = useWindowDimensions();
  const { top } = useSafeAreaInsets();

  const { showError } = useEndFlowModal();
  const { showInfo } = useInfoModal();

  const { mutateAsync: getNonProfitPaymentMethod, isPending } = useGetNonProfitPaymentMethods();
  const { mutateAsync: createRedirect } = useCreateRedirect();

  const isIcacBoolean = isIcac === 'true';

  const [defaultValues, setDefaultValues] = useState<DonationData>({
    whereToDonateId: npoId ?? '-',
    whereToDonate: npoName ?? '',
    amount: amount ?? '0',
    date: new Date().toISOString(),
    giftType: 'one-time',
    optMonthlyUpdates: null,
    giftRecurringOption: undefined,
    notes: undefined
  });

  //Locales
  const registerTitle = i18n.t('register_modal.title');
  const registerSubtitle = i18n.t('register_modal.subtitle');
  const registerButtonLabel = i18n.t('register_modal.button_label');

  useEffect(() => {
    setDefaultValues({
      whereToDonateId: npoId ?? '-',
      whereToDonate: npoName ?? '',
      amount: amount ?? '0',
      date: new Date().toISOString(),
      giftType: 'one-time',
      optMonthlyUpdates: null,
      giftRecurringOption: undefined,
      notes: undefined
    });
  }, [amount, npoId, npoName]);

  const onSubmit = async (data: DonationData) => {
    if (state.user?.user.role !== 'INDIVIDUAL' && state.user?.user.role !== 'NON_PROFIT_ORG') {
      showInfo({
        type: 'full',
        title: registerTitle,
        subtitle: registerSubtitle,
        buttonLabel: registerButtonLabel,
        onButtonPress: () => {
          router.navigate('/');
        }
      });
    } else if (
      data.whereToDonateId !== '-' &&
      data.whereToDonateId !== '-1' &&
      state.user?.user.role !== 'NON_PROFIT_ORG'
    ) {
      if (+data.amount <= 0) {
        showError({
          title: i18n.t('donation.error_title'),
          message: i18n.t('donation.empty_amount')
        });
        return;
      }
      try {
        const donationData = {
          individualUserId: state.user.user.id,
          nonProfitOrgId: data.whereToDonateId,
          giftType: data.giftType ?? 'one-time',
          amount: data.amount,
          date: data.date ?? new Date().toISOString(),
          whereToDonate: data.whereToDonate,
          notes: data.notes,
          ...(data.optMonthlyUpdates === true && { optIn: data.optMonthlyUpdates }),
          ...(data.giftType !== 'one-time' && { giftRecurringOption: data.giftRecurringOption })
        };

        getNonProfitPaymentMethod(
          {
            id: data.whereToDonateId
          },
          {
            onSuccess: async (data) => {
              // Making donations outside iOS App
              if (Platform.OS === 'ios') {
                createRedirect(undefined, {
                  onSuccess: async (redirect) => {
                    const url = APPSFLYER_CONFIG.af_web_dp;
                    const payload = {
                      oneTimeToken: redirect.token,
                      donationData: JSON.stringify(donationData),
                      availableMethods: data.paymentMethods,
                      fromIOS: true
                    };
                    const encrypted = await encryptObject(payload);

                    const deepLinkValue = new URLSearchParams({
                      deep_link_value: `paymentMethods?payload=${encodeURIComponent(encrypted)}`
                    });

                    await WebBrowser.openBrowserAsync(`${url}?${deepLinkValue}`);
                  },
                  onError: (e) => {
                    console.error(e);
                  }
                });
              } else {
                const payload = {
                  donationData: JSON.stringify(donationData),
                  availableMethods: data.paymentMethods
                };

                const encrypted = await encryptObject(payload);

                router.navigate({
                  pathname: '/(app)/paymentMethods',
                  params: {
                    payload: encodeURIComponent(encrypted)
                  }
                });
              }
            },
            onError: (e) => {
              console.error(e);
            }
          }
        );
      } catch (error) {
        console.error(error);
        showError({});
      }
    } else {
      showError({
        title: i18n.t('donation.error_title'),
        message: i18n.t('donation.error_message')
      });
    }
  };

  return (
    <PlatformContainer>
      <FormWrapper>
        <View style={[style.container, { paddingTop: Platform.OS === 'android' ? 15 : top + 15 }]}>
          {npoName && (
            <View style={style.header}>
              <View style={style.side}>
                <GoBackButton style={style.goBackButton} />
              </View>
              <View style={style.center}>
                <Title label={i18n.t('bottom_tab.donation')} />
              </View>
              <View style={style.side} />
            </View>
          )}
          {/* Header */}
          {!npoName && (
            <>
              <View style={style.header}>
                <Title label={i18n.t('bottom_tab.donation')} />
              </View>
              <Image
                source={donation}
                style={[Platform.OS !== 'web' && { width: width - 36 }, style.image]}
                contentFit="cover"
              />
              <Text fontWeight="700" style={style.title}>
                {i18n.t('donation.your_support')}
              </Text>
            </>
          )}
          <DonationForm
            loading={isPending}
            initialValues={defaultValues}
            onSubmitTap={onSubmit}
            isIcac={isIcacBoolean}
          />
        </View>
      </FormWrapper>
    </PlatformContainer>
  );
};

export default Donation;

const style = StyleSheet.create({
  safe: {
    flex: 1
  },

  container: {
    width: '100%',
    flex: 1,
    gap: 25,
    paddingBottom: Platform.OS !== 'web' ? 25 : 0
  },

  image: {
    overflow: 'hidden',
    height: 170,
    borderRadius: 10
  },

  header: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center'
  },

  goBackButton: {
    marginLeft: 18,
    alignSelf: 'flex-start'
  },

  side: {
    flex: 1,
    alignItems: 'flex-start',
    paddingLeft: 18
  },

  center: {
    flex: 2,
    alignItems: 'center'
  },

  title: {
    fontSize: 22,
    color: 'black'
  }
});
